import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { downloadProtectedFile, openProtectedFile } from '../../../app/utils/fileDownloads';

export default function AdminTransactions() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyAction, setBusyAction] = useState('');

  const load = () => {
    setLoading(true);
    axios.get('/api/admin/payments', { params: { status: filter } })
      .then((r) => setPayments(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filter]);

  const verify = async (id) => {
    try {
      await axios.put(`/api/admin/payments/${id}/verify`);
      toast.success('Payment verified and receipt generated');
      load();
    } catch {
      toast.error('Verification failed');
    }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this payment?')) return;
    try {
      await axios.put(`/api/admin/payments/${id}/reject`);
      toast.success('Payment rejected');
      load();
    } catch {
      toast.error('Failed to reject payment');
    }
  };

  const viewScreenshot = async (id) => {
    setBusyAction(`screenshot-${id}`);
    try {
      await openProtectedFile(`/api/admin/payments/${id}/screenshot`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to open screenshot');
    } finally {
      setBusyAction('');
    }
  };

  const downloadReceipt = async (id) => {
    setBusyAction(`receipt-${id}`);
    try {
      await downloadProtectedFile(`/api/admin/payments/${id}/receipt`, `receipt-${id}.pdf`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download receipt');
    } finally {
      setBusyAction('');
    }
  };

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn">&larr;</Link>
        <h1>Transactions</h1>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', overflowX: 'auto' }}>
        {['pending', 'verified', 'rejected', 'all'].map((value) => {
          const isActive = filter === value || (value === 'all' && !filter);
          return (
            <button
              key={value}
              onClick={() => setFilter(value === 'all' ? '' : value)}
              className="btn btn-sm"
              style={{
                background: isActive ? 'var(--steel-soft)' : 'var(--surface)',
                color: isActive ? 'white' : 'var(--muted)',
                border: '1px solid var(--border)',
                whiteSpace: 'nowrap',
              }}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </button>
          );
        })}
      </div>
      <div className="page-content" style={{ paddingTop: 0 }}>
        {loading ? (
          <div className="loading-center"><div className="spinner spinner-dark" /></div>
        ) : payments.length === 0 ? (
          <div className="empty-state"><div className="icon">Money</div><p>No transactions</p></div>
        ) : payments.map((payment) => (
          <div className="card" key={payment.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 12 }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 14 }}>{payment.full_name}</p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>{payment.shipping_mark} · {payment.payment_type}</p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(payment.created_at).toLocaleDateString('en-GH')}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontWeight: 800, fontSize: 16, color: 'var(--steel-soft)' }}>GHS {parseFloat(payment.amount).toFixed(2)}</p>
                <span className={`badge badge-${payment.status}`}>{payment.status}</span>
              </div>
            </div>

            {payment.screenshot_path && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                style={{ marginBottom: 8 }}
                onClick={() => viewScreenshot(payment.id)}
                disabled={busyAction === `screenshot-${payment.id}`}
              >
                {busyAction === `screenshot-${payment.id}` ? 'Opening...' : 'View Screenshot'}
              </button>
            )}

            {payment.status === 'pending' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => verify(payment.id)} className="btn btn-success btn-sm" style={{ flex: 1 }}>Verify</button>
                <button onClick={() => reject(payment.id)} className="btn btn-danger btn-sm" style={{ flex: 1 }}>Reject</button>
              </div>
            )}

            {payment.status === 'verified' && payment.receipt_path && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => downloadReceipt(payment.id)}
                disabled={busyAction === `receipt-${payment.id}`}
              >
                {busyAction === `receipt-${payment.id}` ? 'Downloading...' : 'Download Receipt'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
